import {
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import { ControlValueAccessor, FormBuilder, FormGroup } from "@angular/forms";
import { ValidationResult, Validator } from "@lchemy/model/validation";
import { BehaviorSubject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";

/**
 * Extending this will give you the FormControl logic
 * You still need to add the following to your component providers:
 */
// {
// 	provide: NG_VALUE_ACCESSOR,
// 	useExisting: forwardRef(() => MyFormControlComponent),
// 	multi: true
// }
@Directive()
export abstract class FormGroupComponent<T>
  implements ControlValueAccessor, OnDestroy, OnInit
{
  get value(): Partial<T> {
    return this.formGroup.value;
  }

  @Output()
  statusChanges = new EventEmitter<boolean>();

  /*
   *	A component that extends this class must define formControlNames. These control names are used to create
   *	the controls of the form group in the base class. Be sure to type your formControlNames in your extending class!
   */
  protected abstract formControlNames: Array<keyof T>;

  formGroup: FormGroup;

  // @ts-ignore
  lchemyValidator?: Validator<T>;

  @Input()
  get lchemyValidationResult(): ValidationResult<T> {
    return this._lchemyValidationResult;
  }
  set lchemyValidationResult(lchemyValidationResult: ValidationResult<T>) {
    this._lchemyValidationResult = lchemyValidationResult;

    if (
      lchemyValidationResult.isValid ||
      lchemyValidationResult.errors == null
    ) {
      this.formGroup.setErrors(null);
    } else {
      this.formGroup.setErrors(lchemyValidationResult.errors);
    }
  }
  private _lchemyValidationResult: ValidationResult<T> =
    ValidationResult.create(null, {});

  @Input()
  forceShowErrors = false;

  protected constructor(protected fb: FormBuilder) {}

  ngOnInit(): void {
    this.initForm();
  }

  protected onDestroyEmitter = new EventEmitter<void>();
  ngOnDestroy(): void {
    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();
  }

  protected initForm(): void {
    const formGroupSchema = this.formControlNames.reduce(
      (accum, name) => ({ ...accum, [name]: undefined }), // null here allows parent FormGroup to provide default value
      {}
    );
    this.formGroup = this.fb.group(formGroupSchema);
    this.initLchemyValidation();
    this.broadcastInitted();
  }
  protected formInitted$ = new BehaviorSubject(false);
  protected onInitFormEmitter = new EventEmitter<void>();

  private broadcastInitted(): void {
    // NB: keep formInitted and onInitFormEmitter together
    this.onInitFormEmitter.emit();
    this.formInitted$.next(true);
  }

  initLchemyValidation(): void {
    this.formGroup.valueChanges
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe(() => {
        this.validateWithLchemy();
      });
  }

  writeValue(val: Partial<T>): void {
    if (val == null) {
      val = {};
    }

    const newVal = Object.keys(this.formGroup.controls).reduce((acc, key) => {
      return { ...acc, [key]: val[key] };
    }, {});

    this.formGroup.patchValue(newVal);
  }

  registerOnChange(propagateChanges: any): void {
    this.formGroup.valueChanges
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe(propagateChanges);
  }

  registerOnTouched(touchCb: any): void {
    this.formGroup.valueChanges
      .pipe(first(), takeUntil(this.onDestroyEmitter))
      .subscribe(touchCb);
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.formGroup.disable() : this.formGroup.enable();
  }

  protected async validateWithLchemy(): Promise<void> {
    if (this.lchemyValidator != null) {
      this.lchemyValidationResult = await this.lchemyValidator!.validate(
        // TODO: what????
        // disabled controls will not be included in formGroup.value,
        // so we need to use formGroup.getRawValue() instead
        this.formGroup.getRawValue() as T
      );
    }
  }
}
